import React, { useEffect, useRef, useState } from "react";
import { marked } from "marked";

const WEBHOOK_URL =
  "https://n8n.prayog.io/webhook/smile-customer-success-operator";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const toggleChat = () => setOpen((o) => !o);

  const appendMessage = (content, type = "user") => {
    setMessages((prev) => [...prev, { content, type }]);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    appendMessage(input, "user");
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chatInput: input }),
      });

      const data = await res.json();

      /**
       * 🔥 Normalize API response
       */
      const payload =
        Array.isArray(data) && data[0]?.output ? data[0].output : data.output || data;

      appendMessage(payload, "bot");
    } catch {
      appendMessage("⚠️ Failed to send message", "bot");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Icon */}
      <div
        onClick={toggleChat}
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "#854fff",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 24,
          cursor: "pointer",
          zIndex: 9999,
        }}
      >
        💬
      </div>

      {/* Chat Window */}
      {open && (
        <div
          style={{
            position: "fixed",
            bottom: 90,
            right: 20,
            width: 380,
            height: 520,
            maxHeight: "70vh",
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 10px 30px rgba(0,0,0,.15)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 9999,
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "#854fff",
              color: "#fff",
              padding: 14,
              fontWeight: 600,
            }}
          >
            Customer Success AI Support
          </div>

          {/* Messages */}
          <div
            ref={bodyRef}
            style={{
              flex: 1,
              padding: 12,
              overflowY: "auto",
              background: "#fafafa",
            }}
          >
            {messages.map((m, i) => (
              <div key={i} className="chat-bubble">
                {/* ✅ Button response */}
                {typeof m.content === "object" && m.content.type === "button" ? (
                  <>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: marked.parse(m.content.text || ""),
                      }}
                    />
                    <a
                      href={m.content.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="chat-action-button"
                    >
                      {m.content.label || "Open"}
                    </a>
                  </>
                ) : (
                  /* ✅ Normal markdown/table */
                  <div
                    dangerouslySetInnerHTML={{
                      __html: marked.parse(String(m.content)),
                    }}
                  />
                )}
              </div>
            ))}

            {/* Typing Loader */}
            {loading && (
              <div className="typing-indicator">
                <span />
                <span />
                <span />
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{ display: "flex", borderTop: "1px solid #eee" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Type your message..."
              style={{
                flex: 1,
                border: "none",
                padding: 12,
                fontSize: 14,
                outline: "none",
              }}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              style={{
                background: "#854fff",
                color: "#fff",
                border: "none",
                padding: "0 16px",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        .chat-bubble {
          background: #fff;
          border-radius: 8px;
          padding: 10px;
          margin-bottom: 10px;
          font-size: 14px;
          line-height: 1.5;
        }

        .chat-action-button {
          display: inline-block;
          margin-top: 8px;
          padding: 8px 14px;
          background: #854fff;
          color: #fff;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 600;
        }

        .chat-action-button:hover {
          background: #6b3fd4;
        }

        .chat-bubble table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 8px;
          font-size: 13px;
          display: block;
          overflow-x: auto;
        }

        .chat-bubble th,
        .chat-bubble td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }

        .chat-bubble th {
          background: #f4f3ff;
          font-weight: 600;
        }

        .typing-indicator {
          background: #f0f0f0;
          padding: 10px 14px;
          border-radius: 18px;
          width: 60px;
          display: flex;
          gap: 6px;
        }

        .typing-indicator span {
          width: 8px;
          height: 8px;
          background: #854fff;
          border-radius: 50%;
          animation: blink 1.4s infinite both;
        }

        .typing-indicator span:nth-child(2) { animation-delay: .2s }
        .typing-indicator span:nth-child(3) { animation-delay: .4s }

        @keyframes blink {
          0% { opacity: .2 }
          20% { opacity: 1 }
          100% { opacity: .2 }
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
